import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '../services/api'

// Client-side mirror of backend TIER_CONFIG
export const TIER_CONFIG = {
  free: {
    credits_per_month: 10,
    analytics_days: 28,
    max_channels: 1,
    watermark_exports: true,
    all_templates: false,
    label: 'Free',
    price_monthly: 0,
  },
  pro: {
    credits_per_month: 200,
    analytics_days: null,
    max_channels: 3,
    watermark_exports: false,
    all_templates: true,
    label: 'Pro',
    price_monthly: 29,
  },
  agency: {
    credits_per_month: 800,
    analytics_days: null,
    max_channels: 10,
    watermark_exports: false,
    all_templates: true,
    label: 'Agency',
    price_monthly: 79,
  },
}

const PLAN_HIERARCHY = ['free', 'pro', 'agency']

const BillingContext = createContext(null)

export function BillingProvider({ children }) {
  const [plan, setPlan] = useState('free')
  const [subscription, setSubscription] = useState(null)
  const [credits, setCredits] = useState({ purchased: 0, subscription: 0, total: 0 })
  const [creditPackages, setCreditPackages] = useState([])
  // Precio por acción: lo dicta el backend para que lo que se anuncia y lo que
  // se cobra no puedan separarse.
  const [creditPricing, setCreditPricing] = useState([])
  const [creditWeights, setCreditWeights] = useState({})
  const [hasByok, setHasByok] = useState(false)
  const [stripeConfig, setStripeConfig] = useState(null)
  const [upgradePrompt, setUpgradePrompt] = useState(null) // { feature, required_plan }
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const [statusRes, settingsRes, creditsRes] = await Promise.all([
        api.get('/billing/status'),
        api.get('/user-settings'),
        api.get('/billing/credits'),
      ])
      setPlan(statusRes.data.plan ?? 'free')
      setSubscription(statusRes.data.subscription ?? null)
      setCredits(creditsRes.data.balance ?? { purchased: 0, subscription: 0, total: 0 })
      setCreditPackages(creditsRes.data.packages ?? [])
      setCreditPricing(creditsRes.data.pricing ?? [])
      setCreditWeights(creditsRes.data.weights ?? {})
      setHasByok(Boolean(settingsRes.data?.has_byok_key))
    } catch {
      // Non-critical — keep current state
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (localStorage.getItem('vf_token')) {
      refresh()
    } else {
      setLoading(false)
    }
  }, [refresh])

  useEffect(() => {
    const handler = event => {
      const { required_plan, feature } = event.detail
      setUpgradePrompt({
        feature: feature ?? 'full_analytics',
        required_plan: required_plan ?? 'pro',
      })
    }
    window.addEventListener('billing:upgrade_required', handler)
    return () => window.removeEventListener('billing:upgrade_required', handler)
  }, [])

  useEffect(() => {
    api
      .get('/billing/config')
      .then(res => setStripeConfig(res.data))
      .catch(() => {})
  }, [])

  const canUseFeature = useCallback(
    feature => {
      const config = TIER_CONFIG[plan] ?? TIER_CONFIG.free
      switch (feature) {
        case 'retention':
        case 'traffic_sources':
        case 'full_analytics':
          return isPlanGte(plan, 'pro')
        case 'all_templates':
          return config.all_templates
        case 'multi_channel':
          return config.max_channels > 1
        case 'no_watermark':
          return !config.watermark_exports
        case 'broll':
          return isPlanGte(plan, 'pro')
        default:
          return true
      }
    },
    [plan]
  )

  const openCheckout = useCallback(async priceId => {
    if (!priceId) return
    try {
      const res = await api.post('/billing/checkout', { price_id: priceId })
      window.location.href = res.data.checkout_url
    } catch (err) {
      console.error('Checkout error', err)
    }
  }, [])

  const purchaseCreditPack = useCallback(async packageId => {
    if (!packageId) return
    try {
      const res = await api.post('/billing/checkout/credits', { package_id: packageId })
      window.location.href = res.data.checkout_url
    } catch (err) {
      console.error('Credit pack checkout error', err)
    }
  }, [])

  const openPortal = useCallback(async () => {
    try {
      const res = await api.post('/billing/portal')
      window.location.href = res.data.portal_url
    } catch (err) {
      console.error('Portal error', err)
    }
  }, [])

  const showUpgradePrompt = useCallback((feature, requiredPlan = 'pro') => {
    setUpgradePrompt({ feature, required_plan: requiredPlan })
  }, [])

  const dismissUpgradePrompt = useCallback(() => {
    setUpgradePrompt(null)
  }, [])

  return (
    <BillingContext.Provider
      value={{
        plan,
        subscription,
        credits,
        creditPackages,
        creditPricing,
        creditWeights,
        hasByok,
        stripeConfig,
        loading,
        isFreePlan: plan === 'free',
        isProPlan: plan === 'pro' || plan === 'agency',
        canUseFeature,
        openCheckout,
        purchaseCreditPack,
        openPortal,
        refresh,
        upgradePrompt,
        showUpgradePrompt,
        dismissUpgradePrompt,
      }}
    >
      {children}
    </BillingContext.Provider>
  )
}

export const useBilling = () => useContext(BillingContext)

/**
 * Traduce un saldo de créditos a resultados concretos. Nadie sabe lo que vale
 * un crédito; todo el mundo sabe lo que vale un guion.
 * Devuelve [] si no hay precios cargados todavía.
 */
export function creditsAsResults(total, weights) {
  const out = []
  const long = weights?.script_youtube_long
  const medium = weights?.script_youtube_medium
  const section = weights?.script_section
  if (long > 0) {
    const n = Math.floor(total / long)
    out.push(`${n} ${n === 1 ? 'guion largo' : 'guiones largos'}`)
  }
  if (medium > 0 && medium !== long) {
    const n = Math.floor(total / medium)
    out.push(`${n} ${n === 1 ? 'medio' : 'medios'}`)
  }
  if (section > 0) {
    const n = Math.floor(total / section)
    out.push(`${n} ${n === 1 ? 'regeneración' : 'regeneraciones'}`)
  }
  return out
}

/**
 * Une los resultados con la conjunción que toca. "u 8" y no "o 8": la cifra se
 * lee en voz alta aunque esté escrita en número.
 */
export function joinResults(parts) {
  return parts.reduce((acc, part, i) => {
    if (i === 0) return part
    const conjunction = /^(8|11)\b/.test(part) ? 'u' : 'o'
    return `${acc}, ${conjunction} ${part}`
  }, '')
}

/**
 * El semáforo del saldo. Informa, no reclama: verde mientras haya margen,
 * ámbar cuando queda poco, rojo solo en cero.
 */
export function creditTone(total) {
  if (total <= 0) return '#D42B1E'
  if (total < 4) return '#FF9F0A'
  return '#30D158'
}

/**
 * Etiqueta y estado de un botón que gasta créditos. El coste va delante de la
 * decisión, no detrás: así el choque con el saldo no llega a producirse. Sin
 * saldo el botón deja de prometer una generación y explica qué falta.
 *
 * `blocked` no deshabilita: el botón sigue siendo pulsable para abrir el panel
 * de saldo. Un callejón sin salida no es un estado.
 */
export function generateAction(cost, { hasByok, total, verb = 'Generar' } = {}) {
  if (hasByok || !cost) return { label: verb, blocked: false }
  const unit = cost === 1 ? 'crédito' : 'créditos'
  if (typeof total === 'number' && total < cost) {
    return { label: `Necesitas ${cost} ${unit}`, blocked: true }
  }
  // Gastar justo lo que queda se avisa antes, no después.
  const suffix =
    typeof total === 'number' && total === cost ? `tus últimos ${cost}` : `${cost} ${unit}`
  return { label: `${verb} · ${suffix}`, blocked: false }
}

/**
 * El coste como coletilla, para controles pequeños donde no cabe una etiqueta
 * entera. Devuelve '' cuando no hay precio que mostrar.
 */
export function costSuffix(cost, hasByok) {
  if (hasByok || !cost) return ''
  return ` · ${cost} ${cost === 1 ? 'crédito' : 'créditos'}`
}

function isPlanGte(userPlan, requiredPlan) {
  const userRank = PLAN_HIERARCHY.indexOf(userPlan)
  const requiredRank = PLAN_HIERARCHY.indexOf(requiredPlan)
  return userRank >= requiredRank
}
